import React, { useState, useEffect } from 'react';
import { socialMediaService, SocialMediaUpload } from '../../../shared/services/socialMediaService';
import Toast from '../../../shared/components/ui/Toast';
import Button from '../../../shared/components/ui/Button';
import Modal from '../../../shared/components/ui/Modal';

interface SocialMediaUploadDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  uploadId: string | null;
  onDelete?: (uploadId: string) => void;
  onRetry?: (uploadId: string) => void;
}

const SocialMediaUploadDrawer: React.FC<SocialMediaUploadDrawerProps> = ({
  isOpen,
  onClose,
  uploadId,
  onDelete,
  onRetry,
}) => {
  const [upload, setUpload] = useState<SocialMediaUpload | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [toast, setToast] = useState<{ isVisible: boolean; type: 'success' | 'error' | 'info'; message: string }>({
    isVisible: false,
    type: 'info',
    message: '',
  });

  // Load upload data
  useEffect(() => {
    if (!isOpen || !uploadId) {
      setUpload(null);
      return;
    }

    const loadUpload = async () => {
      setIsLoading(true);
      try {
        const result = await socialMediaService.getUploadStatus(uploadId);
        if (result.success && result.data) {
          setUpload(result.data);
        } else {
          setToast({
            isVisible: true,
            type: 'error',
            message: result.error || 'Failed to load upload',
          });
        }
      } catch (error) {
        console.error('Error loading upload:', error);
        setToast({
          isVisible: true,
          type: 'error',
          message: 'Failed to load upload',
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadUpload();

    // Poll for status updates every 3 seconds
    const interval = setInterval(async () => {
      if (uploadId) {
        const result = await socialMediaService.getUploadStatus(uploadId);
        if (result.success && result.data) {
          setUpload(result.data);
        }
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [isOpen, uploadId]);

  const handleDeleteClick = () => {
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!uploadId) return;
    setIsDeleteModalOpen(false);
    
    try {
      const result = await socialMediaService.deleteUpload(uploadId);
      if (result.success) {
        setToast({
          isVisible: true,
          type: 'success',
          message: 'Upload deleted successfully',
        });
        onDelete?.(uploadId);
        onClose();
      } else {
        setToast({
          isVisible: true,
          type: 'error',
          message: result.error || 'Failed to delete upload',
        });
      }
    } catch (error) {
      console.error('Error deleting upload:', error);
      setToast({
        isVisible: true,
        type: 'error',
        message: 'Failed to delete upload',
      });
    }
  };

  const handleRetryClick = async () => {
    if (!uploadId) return;
    
    try {
      const result = await socialMediaService.retryUpload(uploadId);
      if (result.success) {
        setToast({
          isVisible: true,
          type: 'success',
          message: 'Upload retry initiated',
        });
        onRetry?.(uploadId);
        // Reload upload status
        const statusResult = await socialMediaService.getUploadStatus(uploadId);
        if (statusResult.success && statusResult.data) {
          setUpload(statusResult.data);
        }
      } else {
        setToast({
          isVisible: true,
          type: 'error',
          message: result.error || 'Failed to retry upload',
        });
      }
    } catch (error) {
      console.error('Error retrying upload:', error);
      setToast({
        isVisible: true,
        type: 'error',
        message: 'Failed to retry upload',
      });
    }
  };

  const handleOpenPlatform = () => {
    if (upload?.platform_url) {
      window.open(upload.platform_url, '_blank');
    }
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      pending: 'bg-gray-100 text-gray-800 border border-gray-200',
      queued: 'bg-gray-100 text-gray-800 border border-gray-200',
      uploading: 'bg-blue-100 text-blue-800 border border-blue-200',
      processing: 'bg-yellow-100 text-yellow-800 border border-yellow-200',
      completed: 'bg-green-100 text-green-800 border border-green-200',
      failed: 'bg-red-100 text-red-800 border border-red-200',
      cancelled: 'bg-gray-100 text-gray-800 border border-gray-200',
    };
    return styles[status as keyof typeof styles] || styles.pending;
  };

  const formatDate = (iso?: string): string => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString();
  };

  if (!isOpen || !uploadId) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 overflow-hidden">
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />

        {/* Drawer */}
        <div className="absolute right-0 top-0 h-full w-full max-w-2xl bg-white shadow-xl flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Social Media Upload</h2>
              {upload && (
                <p className="text-sm text-gray-500 mt-1 capitalize">{upload.platform}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : !upload ? (
              <div className="text-center py-12">
                <p className="text-gray-500">Upload not found</p>
              </div>
            ) : (
              <>
                {/* Upload Metadata */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Upload Metadata</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs text-gray-500">Title</label>
                      <p className="text-sm text-gray-900 mt-1">{upload.metadata?.title || '—'}</p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Description</label>
                      <p className="text-sm text-gray-900 mt-1 whitespace-pre-wrap">
                        {upload.metadata?.description || '—'}
                      </p>
                    </div>
                    {upload.metadata?.tags && upload.metadata.tags.length > 0 && (
                      <div>
                        <label className="text-xs text-gray-500">Tags</label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {upload.metadata.tags.map((tag, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {upload.metadata?.finalScore && (
                      <div>
                        <label className="text-xs text-gray-500">Final Score</label>
                        <p className="text-sm text-gray-900 mt-1">{upload.metadata.finalScore}</p>
                      </div>
                    )}
                    {upload.metadata?.hashtags && (
                      <div>
                        <label className="text-xs text-gray-500">Hashtags</label>
                        <p className="text-sm text-gray-900 mt-1">{upload.metadata.hashtags}</p>
                      </div>
                    )}
                    {upload.metadata?.privacyStatus && (
                      <div>
                        <label className="text-xs text-gray-500">Privacy Setting</label>
                        <p className="text-sm text-gray-900 mt-1 capitalize">{upload.metadata.privacyStatus}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Platform Information */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Platform Information</h3>
                  <div className="space-y-3">
                    <div className="flex items-start justify-between">
                      <span className="text-sm text-gray-500">Platform</span>
                      <span className="text-sm text-gray-900 capitalize">{upload.platform}</span>
                    </div>
                    <div className="flex items-start justify-between">
                      <span className="text-sm text-gray-500">Status</span>
                      <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadge(upload.upload_status)}`}>
                        {upload.upload_status.charAt(0).toUpperCase() + upload.upload_status.slice(1)}
                      </span>
                    </div>
                    <div className="flex items-start justify-between">
                      <span className="text-sm text-gray-500">Uploaded At</span>
                      <span className="text-sm text-gray-900">
                        {formatDate(upload.upload_completed_at || upload.created_at)}
                      </span>
                    </div>
                    {upload.platform_url && (
                      <div className="flex items-start justify-between">
                        <span className="text-sm text-gray-500">Platform URL</span>
                        <a
                          href={upload.platform_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
                        >
                          Open on {upload.platform}
                        </a>
                      </div>
                    )}
                    {upload.platform_video_id && (
                      <div className="flex items-start justify-between">
                        <span className="text-sm text-gray-500">Platform Video ID</span>
                        <span className="text-sm text-gray-900 font-mono">{upload.platform_video_id}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Error Details */}
                {upload.upload_status === 'failed' && upload.error_message && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Error Details</h3>
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-800">{upload.error_message}</p>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="pt-4 border-t border-gray-200 space-y-2">
                  {upload.platform_url && (
                    <Button
                      onClick={handleOpenPlatform}
                      variant="primary"
                      className="w-full"
                    >
                      Open on {upload.platform}
                    </Button>
                  )}
                  {upload.upload_status === 'failed' && (
                    <Button
                      onClick={handleRetryClick}
                      variant="primary"
                      className="w-full"
                    >
                      Retry Upload
                    </Button>
                  )}
                  <Button
                    onClick={handleDeleteClick}
                    variant="ghost"
                    className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    Delete Upload
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <Modal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          title="Delete Upload"
        >
          <div className="p-6 space-y-4">
            <p className="text-sm text-gray-700">
              Are you sure you want to delete this upload?
            </p>
            <p className="text-xs text-gray-500">
              This action cannot be undone. The upload record will be permanently deleted.
            </p>
            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setIsDeleteModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
              >
                Delete Upload
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Toast */}
      {toast.isVisible && (
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={toast.isVisible}
          onClose={() => setToast({ ...toast, isVisible: false })}
        />
      )}
    </>
  );
};

export default SocialMediaUploadDrawer;

